/* @jsx h */
import { calculateOrderItemTotal, formatPrice } from "@lemmonhq/orders";
import { Order, PaymentDetails, RestaurantProfile } from "@lemmonhq/types";
import { computed, defineComponent, h } from "vue";
import { groupOrderItems } from "../printer/templates/helpers";

export type PaymentReceiptProps = {
  payment: PaymentDetails;
  shop: RestaurantProfile;
  orders?: Order[];
  relatedPayment?: PaymentDetails;
  labels?: Partial<PaymentReceiptLabels>;
  locale?: string;
};

export type PaymentReceiptLabels = {
  receipt: string;
  creditNote: string;
  tel: string;
  vat: string;
  paymentMethod: string;
  table: string;
  transactionId: string;
  type: string;
  status: string;
  items: string;
  taxRate: string;
  creditNoteInformation: string;
  creditNoteForPayment: string;
  splitPayment: string;
  splitPaymentCopy: string;
  ofTotal: string;
  partialPayment: string;
  partialPaymentCopy: string;
  vatBreakdown: string;
  net: string;
  subtotal: string;
  discount: string;
  tips: string;
  rewards: string;
  deliveryFee: string;
  total: string;
  totalPaid: string;
};

const defaultLabels: PaymentReceiptLabels = {
  receipt: "Receipt",
  creditNote: "Credit Note",
  tel: "Tel",
  vat: "VAT",
  paymentMethod: "Payment Method",
  table: "Table",
  transactionId: "Transaction ID",
  type: "Type",
  status: "Status",
  items: "Items",
  taxRate: "Tax Rate",
  creditNoteInformation: "Credit Note Information",
  creditNoteForPayment: "This is a credit note for payment #{0}",
  splitPayment: "Split Payment",
  splitPaymentCopy: "This is a {0} split payment",
  ofTotal: "of total",
  partialPayment: "Partial Payment",
  partialPaymentCopy:
    "This receipt shows only the items included in this partial payment.",
  vatBreakdown: "VAT Breakdown",
  net: "Net",
  subtotal: "Subtotal",
  discount: "Discount",
  tips: "Tips",
  rewards: "Rewards",
  deliveryFee: "Delivery Fee",
  total: "Total",
  totalPaid: "Total Paid",
};

export const PaymentReceiptMain = defineComponent<PaymentReceiptProps>({
  name: "PaymentReceiptMain",
  props: ["payment", "shop", "relatedPayment", "orders", "labels", "locale"],
  setup(props) {
    const labels = computed(() => ({ ...defaultLabels, ...props.labels }));

    const paymentDate = computed(() =>
      new Date(props.payment.created_at).toLocaleDateString(
        props.locale || "en",
        {
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        },
      ),
    );

    const receiptTitle = computed(() => {
      if (props.payment.type === "credit") {
        return labels.value.creditNote;
      }
      return labels.value.receipt;
    });
    const orderItemsData = computed(() => {
      const items =
        props.orders?.map((order) => order.items || []).flat() || [];

      // For partial payments, show only the relevant items
      if (props.payment.partial_payment?.items) {
        const partialMap: { [key: string]: number } = {};
        props.payment.partial_payment.items.forEach((item) => {
          partialMap[item.id] = item.quantity;
        });
        return groupOrderItems(items, partialMap);
      }

      return groupOrderItems(items);
    });
    const totals = computed(() => {
      const subtotal = orderItemsData.value.groups.reduce(
        (sum, item) => sum + calculateOrderItemTotal(item, item.quantity),
        0,
      );
      const discount = props.payment.discount?.net || 0;
      const tips = props.payment.tips || 0;
      const total = subtotal - discount + tips;
      const totalPaid = props.payment.amount + tips;

      return { subtotal, discount, tips, total, totalPaid };
    });

    const currency = computed(() => "CHF"); // TODO: Get from shop settings
    return () => {
      return (
        <div class=" shadow-xl rounded-lg overflow-hidden">
          {/* Header */}
          <div class="  p-6">
            <div class="flex items-center justify-between">
              <div class="text-accent">
                <h1 class="text-2xl font-bold">{receiptTitle.value}</h1>
                <p class="opacity-90">#{props.payment.guid}</p>
              </div>
              <div class="text-right">
                <p class="text-sm opacity-90">{paymentDate.value}</p>
                <div
                  class={`badge ${
                    props.payment.status === "success"
                      ? "badge-success"
                      : "badge-warning"
                  } mt-2 uppercase`}
                >
                  {props.payment.status}
                </div>
              </div>
            </div>
          </div>

          {/* Shop Information */}
          <div class="p-6 border-b">
            <h2 class="text-xl font-semibold text-accent">{props.shop.name}</h2>
            <p class="text-base-content">{props.shop.address}</p>
            {props.shop.receipt_phone && (
              <p class="text-base-content">
                {labels.value.tel}: {props.shop.receipt_phone}
              </p>
            )}
            {props.shop.company_registration && (
              <p class="text-base-content text-sm">
                {labels.value.vat}: {props.shop.company_registration}
              </p>
            )}
          </div>

          {/* Payment Details */}
          <div class="p-6 border-b">
            <div class="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span class="font-medium">{labels.value.paymentMethod}:</span>
                <p class="text-accent capitalize">{props.payment.method}</p>
              </div>
              {props.payment.table && (
                <div>
                  <span class="font-medium">{labels.value.table}:</span>
                  <p class="text-accent">{props.payment.table.name}</p>
                </div>
              )}
              {props.payment.transaction && (
                <div>
                  <span class="font-medium">{labels.value.transactionId}:</span>
                  <p class="text-accent text-xs">
                    {props.payment.transaction.id || "-"}
                  </p>
                </div>
              )}
              <div>
                <span class="font-medium">{labels.value.type}:</span>
                <p class="text-accent capitalize">{props.payment.type}</p>
              </div>
              <div>
                <span class="font-medium">{labels.value.status}:</span>
                <p class="text-accent capitalize">{props.payment.status}</p>
              </div>
            </div>
          </div>

          {/* Order Items */}
          {orderItemsData.value.groups.length > 0 && (
            <div class="p-6 border-b">
              <h3 class="font-semibold mb-4">{labels.value.items}</h3>
              <div class="space-y-3">
                {orderItemsData.value.groups.map((item, index) => (
                  <div key={index} class="flex justify-between items-start">
                    <div class="flex-1">
                      <div class=" text-accent">{item.product.name}</div>
                      {item.bundles && item.bundles.length > 0 && (
                        <div class="ml-4 mt-1 text-sm text-base-content/70">
                          {item.bundles.map((bundle, bundleIndex) => (
                            <div key={bundleIndex}>
                              - {bundle.display.title}
                              {bundle.price > 0 && (
                                <span class="ml-1">
                                  (+{currency.value} {bundle.price.toFixed(2)})
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {item.taxLabel && (
                        <div class="text-xs mt-1">
                          {labels.value.taxRate}: {item.taxLabel}
                        </div>
                      )}
                    </div>
                    <div class="text-right ml-4">
                      <div class="font-medium">
                        {item.quantity}× {currency.value}{" "}
                        {calculateOrderItemTotal(item, 1).toFixed(2)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Payment Type Specific Information */}
          {props.payment.type === "credit" && props.relatedPayment && (
            <div class="p-6 border-b bg-warning/10">
              <h3 class="font-semibold mb-2 text-warning">
                {labels.value.creditNoteInformation}
              </h3>
              <p class="text-sm">
                {labels.value.creditNoteForPayment.replace(
                  "{0}",
                  props.relatedPayment.uuid.slice(0, 8),
                )}
              </p>
            </div>
          )}

          {props.payment.split_payment && (
            <div class="p-6 border-b bg-info/10">
              <h3 class="font-semibold mb-2 text-info">
                {labels.value.splitPayment}
              </h3>
              <p class="text-sm">
                {labels.value.splitPaymentCopy.replace(
                  "{0}",
                  props.payment.split_payment.split_type,
                )}{" "}
                <span class="text-accent">
                  {formatPrice(
                    props.payment.split_payment.amount,
                    false,
                    2,
                    props.shop.currency || "CHF",
                  )}{" "}
                  {labels.value.ofTotal}
                </span>
              </p>
            </div>
          )}

          {props.payment.partial_payment && (
            <div class="p-6 border-b bg-secondary/10">
              <h3 class="font-semibold mb-2">{labels.value.partialPayment}</h3>
              <p class="text-sm">{labels.value.partialPaymentCopy}</p>
            </div>
          )}

          {/* VAT Breakdown */}
          {orderItemsData.value.taxRates.length > 0 && (
            <div class="p-6 border-b">
              <h3 class="font-semibold mb-4">{labels.value.vatBreakdown}</h3>
              <div class="space-y-3">
                {orderItemsData.value.taxRates.map((taxRate) => {
                  const matchingItems = orderItemsData.value.groups.filter(
                    (item) => item.taxLabel === taxRate.label,
                  );
                  const sum = matchingItems.reduce(
                    (s, i) => s + calculateOrderItemTotal(i, i.quantity),
                    0,
                  );
                  const net = sum / (1 + taxRate.value / 100);
                  const vat = sum - net;
                  return (
                    <div
                      key={taxRate.id}
                      class="grid grid-cols-4 gap-2 text-sm"
                    >
                      <div class="font-medium">
                        {labels.value.vat} {taxRate.label} (
                        {taxRate.value.toFixed(2)}%)
                      </div>
                      <div class="text-right">
                        {labels.value.net}: {currency.value} {net.toFixed(2)}
                      </div>
                      <div class="text-right">
                        {labels.value.vat}: {currency.value} {vat.toFixed(2)}
                      </div>
                      <div class="text-right font-medium">
                        {labels.value.total}: {currency.value} {sum.toFixed(2)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Totals */}
          <div class="p-6">
            <div class="space-y-2">
              <div class="flex justify-between">
                <span>{labels.value.subtotal}:</span>
                <span>
                  {currency.value} {totals.value.subtotal.toFixed(2)}
                </span>
              </div>
              {totals.value.discount > 0 && (
                <div class="flex justify-between text-warning">
                  <span>
                    {labels.value.discount} ({props.payment.discount?.category})
                    {props.payment.discount?.type === "percentage" &&
                      ` ${props.payment.discount.amount}%`}
                    :
                  </span>
                  <span>
                    -{currency.value} {totals.value.discount.toFixed(2)}
                  </span>
                </div>
              )}
              {totals.value.tips > 0 && (
                <div class="flex justify-between">
                  <span>{labels.value.tips}:</span>
                  <span>
                    {currency.value} {totals.value.tips.toFixed(2)}
                  </span>
                </div>
              )}
              {props.payment.rewards?.total ? (
                <div class="flex justify-between">
                  <span>{labels.value.rewards}:</span>
                  <span>
                    {currency.value} {props.payment.rewards.total.toFixed(2)}
                  </span>
                </div>
              ) : null}
              {props.payment.delivery_fee ? (
                <div class="flex justify-between">
                  <span>{labels.value.deliveryFee}:</span>
                  <span>
                    {currency.value} {props.payment.delivery_fee.toFixed(2)}
                  </span>
                </div>
              ) : null}
              <div class="border-t pt-2">
                <div class="flex justify-between text-lg font-bold">
                  <span class="text-accent">{labels.value.totalPaid}:</span>
                  <span class="text-accent">
                    {currency.value} {totals.value.totalPaid.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Message */}
          {props.shop.receipt_message && (
            <div class="p-6 border-t bg-base-200 text-center text-sm">
              <p>{props.shop.receipt_message}</p>
            </div>
          )}
        </div>
      );
    };
  },
});
